import { useCallback, useEffect, useRef, useState } from 'react';

export type DialogRect = {
  left: number;
  top: number;
  width: number;
  height: number;
};

type ResizeHandle = {
  key: 'e' | 's' | 'se' | 'n' | 'w' | 'ne' | 'nw' | 'sw';
  style: React.CSSProperties;
};

type UseDraggableResizableDialogProps = {
  minWidth?: number;
  minHeight?: number;
  initWidth?: number;
  initHeight?: number;
};

export function useDraggableResizableDialog({
  minWidth = 200,
  minHeight = 120,
  initWidth = 300,
  initHeight = 300,
}: UseDraggableResizableDialogProps = {}) {
  const [dlgRect, setDlgRect] = useState<DialogRect>(() => {
    const w = initWidth
      ? Math.min(initWidth, typeof window !== 'undefined' ? window.innerWidth : initWidth)
      : 300;
    const h = initHeight
      ? Math.min(initHeight, typeof window !== 'undefined' ? window.innerHeight : initHeight)
      : 300;
    const left =
      typeof window !== 'undefined' ? Math.max(0, Math.round((window.innerWidth - w) / 2)) : 0;
    const top =
      typeof window !== 'undefined' ? Math.max(0, Math.round((window.innerHeight - h) / 2)) : 0;
    return { left, top, width: Math.max(minWidth, w), height: Math.max(minHeight, h) };
  });

  const clampInside = useCallback(
    (left: number, top: number, width: number, height: number) => {
      const _minWidth = typeof minWidth === 'number' ? minWidth : 0;
      const _minHeight = typeof minHeight === 'number' ? minHeight : 0;

      const viewportW = window.innerWidth;
      const viewportH = window.innerHeight;

      const w = Math.min(Math.max(width, _minWidth), viewportW);
      const h = Math.min(Math.max(height, _minHeight), viewportH);

      const maxLeft = Math.max(0, viewportW - w);
      const maxTop = Math.max(0, viewportH - h);

      const clampedLeft = Math.min(Math.max(left, 0), maxLeft);
      const clampedTop = Math.min(Math.max(top, 0), maxTop);

      return { left: clampedLeft, top: clampedTop, width: w, height: h };
    },
    [minWidth, minHeight],
  );

  const dragState = useRef<{ sx: number; sy: number; sl: number; st: number } | null>(null);

  const onHeaderMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      dragState.current = {
        sx: e.clientX,
        sy: e.clientY,
        sl: dlgRect.left,
        st: dlgRect.top,
      };

      const onMove = (ev: MouseEvent) => {
        if (!dragState.current) return;
        const dx = ev.clientX - dragState.current.sx;
        const dy = ev.clientY - dragState.current.sy;
        const nextLeft = dragState.current.sl + dx;
        const nextTop = dragState.current.st + dy;
        const clamped = clampInside(nextLeft, nextTop, dlgRect.width, dlgRect.height);
        setDlgRect((prev) => ({ ...prev, left: clamped.left, top: clamped.top }));
      };

      const onUp = () => {
        dragState.current = null;
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
      };

      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    },
    [dlgRect.left, dlgRect.top, dlgRect.width, dlgRect.height, clampInside],
  );

  const resizeState = useRef<{
    sx: number;
    sy: number;
    sw: number;
    sh: number;
    edge: ResizeHandle['key'];
  } | null>(null);

  const onResizeMouseDown = useCallback(
    (e: React.MouseEvent, handleKey: ResizeHandle['key']) => {
      e.preventDefault();
      resizeState.current = {
        sx: e.clientX,
        sy: e.clientY,
        sw: dlgRect.width,
        sh: dlgRect.height,
        edge: handleKey,
      };

      const onMove = (ev: MouseEvent) => {
        if (!resizeState.current) return;
        const dx = ev.clientX - resizeState.current.sx;
        const dy = ev.clientY - resizeState.current.sy;

        let nextW = resizeState.current.sw;
        let nextH = resizeState.current.sh;
        let nextLeft = dlgRect.left;
        let nextTop = dlgRect.top;

        if (resizeState.current.edge.includes('e'))
          nextW = Math.max(minWidth, resizeState.current.sw + dx);
        if (resizeState.current.edge.includes('s'))
          nextH = Math.max(minHeight, resizeState.current.sh + dy);
        if (resizeState.current.edge.includes('w')) {
          nextW = Math.max(minWidth, resizeState.current.sw - dx);
          nextLeft = dlgRect.left + dx;
        }
        if (resizeState.current.edge.includes('n')) {
          nextH = Math.max(minHeight, resizeState.current.sh - dy);
          nextTop = dlgRect.top + dy;
        }

        const clamped = clampInside(nextLeft, nextTop, nextW, nextH);
        setDlgRect((prev) => ({
          ...prev,
          left: clamped.left,
          top: clamped.top,
          width: clamped.width,
          height: clamped.height,
        }));
      };

      const onUp = () => {
        resizeState.current = null;
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
      };

      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    },
    [dlgRect.left, dlgRect.top, dlgRect.width, dlgRect.height, minWidth, minHeight, clampInside],
  );

  const resizeHandles: ResizeHandle[] = [
    {
      key: 'e',
      style: { position: 'absolute', top: 0, right: -6, bottom: 0, width: 12, cursor: 'ew-resize' },
    },
    {
      key: 's',
      style: {
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: -6,
        height: 12,
        cursor: 'ns-resize',
      },
    },
    {
      key: 'se',
      style: {
        position: 'absolute',
        right: -8,
        bottom: -8,
        width: 16,
        height: 16,
        cursor: 'nwse-resize',
      },
    },
  ];

  useEffect(() => {
    const handleResize = () => {
      setDlgRect((prev) => {
        const width = Math.min(prev.width, window.innerWidth);
        const height = Math.min(prev.height, window.innerHeight);
        const { left, top, width: w, height: h } = clampInside(prev.left, prev.top, width, height);
        return { left, top, width: w, height: h };
      });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [clampInside]);

  const setDlgSize = useCallback(
    (width: number, height: number) => {
      setDlgRect((prev) => {
        const { left, top, width: w, height: h } = clampInside(prev.left, prev.top, width, height);
        return { left, top, width: w, height: h };
      });
    },
    [clampInside],
  );

  return { dlgRect, onHeaderMouseDown, resizeHandles, onResizeMouseDown, setDlgSize };
}
